import type {
  BillingPayResponse,
  DemoInvoiceRequest,
  PublicBillingInvoice,
  PublicInvoice,
  PublicPaymentLink,
  PublicRefundClaim,
  PublicSubscription,
  PublicTopUp,
  SignupInfo,
  SignupRequest,
  SignupResponse,
  SolanaPrep,
} from "../lib/types";

export class ApiError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly status: number | null = null,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export interface InvoiceStreamHandlers {
  /** A status change (often partial) arrived on the stream. */
  onEvent(patch: Partial<PublicInvoice>): void;
  onOpen(): void;
  /**
   * The stream dropped; the caller polls meanwhile. `closed` = the browser gave up on it for good
   * (HTTP error, unsupported), so the caller must open a new one later; otherwise it retries itself.
   */
  onError(closed: boolean): void;
}

/** Everything the checkout asks of the server (docs/CONTRACT.md, "Server API", public routes). */
export interface CheckoutApi {
  getInvoice(id: string, signal?: AbortSignal): Promise<PublicInvoice>;
  /** GET /public/config — raw JSON, validated by branding/config.ts. 404 on servers without it. */
  getPublicConfig(signal?: AbortSignal): Promise<unknown>;
  /** Returns an unsubscribe function. */
  subscribe(id: string, handlers: InvoiceStreamHandlers): () => void;
  /** Tell the server which transaction to look at. The server only reads chain state. */
  hint(id: string, txHash: string): Promise<void>;
  solanaPrep(id: string): Promise<SolanaPrep>;
  createDemoInvoice(body: DemoInvoiceRequest): Promise<{ id: string }>;

  // ---- public pages ----
  getPaymentLink(handle: string, slug: string, signal?: AbortSignal): Promise<PublicPaymentLink>;
  /** Creates a normal invoice for the link. `amount` in base units (open links only). */
  payPaymentLink(handle: string, slug: string, body: { amount?: string; email?: string }): Promise<BillingPayResponse>;
  getBillingInvoice(token: string, signal?: AbortSignal): Promise<PublicBillingInvoice>;
  payBillingInvoice(token: string): Promise<BillingPayResponse>;
  /** Absolute URL of the server-rendered PDF (or `?format=print` HTML). */
  billingPdfUrl(pdfPath: string, print?: boolean): string;
  getSubscription(token: string, signal?: AbortSignal): Promise<PublicSubscription>;
  cancelSubscription(token: string): Promise<PublicSubscription>;
  getRefundClaim(token: string, signal?: AbortSignal): Promise<PublicRefundClaim>;
  claimRefund(token: string, body: { address: string; addressConfirm: string }): Promise<PublicRefundClaim>;
  getTopUp(id: string, signal?: AbortSignal): Promise<PublicTopUp>;
  getSignupInfo(signal?: AbortSignal): Promise<SignupInfo>;
  signup(body: SignupRequest): Promise<SignupResponse>;
}
